/** Validate current edit tab before switching (Angular tabChange when isEdit). */

import {
  validateAboutYourselfTab,
  isOutsideUSWithoutAlternate,
  type AboutYourselfFormData,
} from "./aboutYourselfValidator";
import {
  getPropertyCoResidentInputs,
  resolvePropertyOtherIndividualsFlag,
  validatePropertyTab,
  resolvePropertyAgentFields,
  isRentLeaseReasonActiveInFormData,
  type PropertyTabFormData,
  type PropertyCoResidentInput,
  type PropertyApplicationContext,
} from "./propertyTabValidator";
import {
  validateIncomeEmploymentTab,
} from "./incomeEmploymentValidator";
import { buildIncomeEmploymentEffectiveData } from "../components/tabs/income-employment/employmentFormModel";
import type {
  ApplicationDetailData,
  ApplicationLayerData,
} from '../types/application'
import type { EditableApplicationDetailTab, TabSwitchValidationResult } from '../types/utils'

export function validateEditableTabBeforeSwitch(
  selectedTab: EditableApplicationDetailTab,
  formData: Record<string, unknown>,
  applicationData: ApplicationLayerData | null | undefined,
  applicationAllData: ApplicationDetailData | null | undefined,
): TabSwitchValidationResult {
  const appData = applicationData ?? {};
  const allData = applicationAllData ?? {};

  switch (selectedTab) {
    case "aboutyourselfinfo": {
      const effective: AboutYourselfFormData = {
        first_name: (formData.first_name as string | undefined) ?? (appData.user_data as { first_name?: string } | undefined)?.first_name,
        middle_name:
          (formData.middle_name as string | undefined) ??
          (appData.user_data as { middle_name?: string } | undefined)?.middle_name,
        last_name:
          (formData.last_name as string | undefined) ??
          (appData.user_data as { last_name?: string } | undefined)?.last_name,
        suffix:
          (formData.suffix as string | undefined) ??
          (appData.user_data as { suffix?: string } | undefined)?.suffix,
        mobile_no:
          (formData.mobile_no as string | undefined) ??
          (appData.user_data as { mobile_no?: string } | undefined)?.mobile_no,
        shared_phone_no:
          (formData.shared_phone_no as string | boolean | undefined) ??
          (appData.user_data as { shared_phone_no?: string } | undefined)?.shared_phone_no,
        email:
          (formData.email as string | undefined) ??
          (appData.user_data as { email?: string } | undefined)?.email,
        communication_preference:
          (formData.communication_preference as string | undefined) ??
          (appData.communication_preference as string | undefined),
        birthdate: (formData.birthdate as string | undefined) ?? (appData.birthdate as string | undefined),
        security_number:
          (formData.security_number as string | undefined) ?? (appData.security_number as string | undefined),
        non_us_address: (() => {
          const v =
            (formData.non_us_address as string | boolean | undefined) ??
            (appData.non_us_address as string | boolean | undefined);
          if (v === true || v === "1") return "1";
          if (v === false || v === "0") return "0";
          if (typeof v === "string") return v;
          return undefined;
        })(),
        current_address:
          (formData.current_address as AboutYourselfFormData["current_address"]) ??
          (appData.current_address as AboutYourselfFormData["current_address"]),
        previous_address:
          (formData.previous_address as AboutYourselfFormData["previous_address"]) ??
          (appData.previous_address as AboutYourselfFormData["previous_address"]) ??
          [],
        alternate_address:
          (formData.alternate_address as AboutYourselfFormData["alternate_address"]) ??
          (appData.alternate_address as AboutYourselfFormData["alternate_address"]) ??
          [],
      };

      if (isOutsideUSWithoutAlternate(effective)) {
        return { ok: false, kind: "about_address" };
      }
      const result = validateAboutYourselfTab(effective);
      if (!result.isValid) {
        return { ok: false, kind: "validation", errors: result.errors };
      }
      return { ok: true };
    }
    case "propertyinfo": {
      const propertyEffective: PropertyTabFormData = {
        has_applied: (formData.has_applied as boolean | null | undefined) ?? null,
        property_name:
          (formData.property_name as string | undefined) ??
          (allData.property as { property_name?: string } | undefined)?.property_name ??
          (allData.property_name as string | undefined) ??
          "",
        property_state: Object.prototype.hasOwnProperty.call(formData, "property_state")
          ? ((formData.property_state as string | undefined) ?? "")
          : ((allData.property as { property_state?: string } | undefined)?.property_state ??
              (allData.property_state as string | undefined) ??
              ""),
        rent_amount: Object.prototype.hasOwnProperty.call(formData, "rent_amount")
          ? ((formData.rent_amount as string | number | null | undefined) ?? "")
          : (allData.rent_amount as string | number | undefined),
        lease_start_date: Object.prototype.hasOwnProperty.call(formData, "lease_start_date")
          ? ((formData.lease_start_date as string | null | undefined) ?? "")
          : (allData.lease_start_date as string | undefined),
        lease_duration:
          (formData.lease_duration as string | number | undefined) ??
          (allData.lease_duration as string | number | undefined),
        unit_number:
          (formData.unit_number as string | undefined) ?? (allData.unit_number as string | undefined),
        rent_lease_reason: formData.rent_lease_reason as string | undefined,
        invite_code: formData.invite_code as string | undefined,
        other_individuals: resolvePropertyOtherIndividualsFlag(
          formData.other_individuals,
          appData,
          allData,
        ),
        co_residents: (Object.prototype.hasOwnProperty.call(
          formData,
          "co_residents",
        )
          ? formData.co_residents
          : getPropertyCoResidentInputs(allData)) as PropertyCoResidentInput[],
        ...resolvePropertyAgentFields(
          {
            agent_id: formData.agent_id as string | undefined,
            agent_name: formData.agent_name as string | undefined,
            agent_email: formData.agent_email as string | undefined,
            agent_phone: formData.agent_phone as string | undefined,
          },
          allData,
        ),
      };

      const propertyContext: PropertyApplicationContext = {
        primaryEmail:
          (appData.user_data as { email?: string } | undefined)?.email ??
          (appData.email as string | undefined),
        primaryMobile:
          (appData.user_data as { mobile_no?: string } | undefined)?.mobile_no ??
          (appData.mobile_no as string | undefined),
        requireRentLeaseReason: isRentLeaseReasonActiveInFormData(formData),
        coResidentInviteRelaxed: Boolean(
          (allData as { invite_code?: string }).invite_code,
        ),
      };

      const propResult = validatePropertyTab(propertyEffective, propertyContext);
      if (!propResult.isValid) {
        return { ok: false, kind: "validation", errors: propResult.errors };
      }
      return { ok: true };
    }
    case "employmentinfo": {
      const incomeEffective = buildIncomeEmploymentEffectiveData(
        formData,
        appData as Record<string, unknown>,
      );

      const incomeResult = validateIncomeEmploymentTab(incomeEffective);
      if (!incomeResult.isValid) {
        return { ok: false, kind: "validation", errors: incomeResult.errors };
      }
      return { ok: true };
    }
    default:
      return { ok: true };
  }
}
